import { CompareProduct } from '@/compare/types';
import { getCompareProducts, setCompareProducts } from '../storage/compare-storage';
import { createElementFromHTML } from '../dom/dom-utils';

const MAX_COMPARE = 3;

export function renderComparePanel(isMinimized: boolean, isLimitErrorVisible: boolean, open: boolean) {
  const panel = document.getElementById('compare-panel');
  const full = document.getElementById('compare-panel-full');
  const mini = document.getElementById('compare-panel-minimized');
  const itemsContainer = document.getElementById('compare-items-container');

  if (!panel || !full || !mini || !itemsContainer) return;

  // Shared products logic
  const sharedRaw = sessionStorage.getItem('shared_compare_products');
  let sharedProducts: CompareProduct[] | null = null;
  if (sharedRaw) {
    try {
      const parsed = JSON.parse(sharedRaw);
      if (Array.isArray(parsed) && parsed.length > 0) sharedProducts = parsed;
    } catch {
      sharedProducts = null;
    }
  }

  const products = sharedProducts || getCompareProducts();

  // 1. Visibility logic
  if (products.length === 0) {
    panel.style.display = 'none';
    itemsContainer.innerHTML = '';
    return;
  }

  panel.style.display = open ? 'block' : 'none';

  // 2. Minimized/Expanded view logic
  const miniText = document.getElementById('compare-minimized-text');
  if (miniText) {
    miniText.textContent = `${window.compareTranslations?.open_compare} (${products.length}/${MAX_COMPARE})`;
  }

  if (isMinimized) {
    full.classList.add('hidden');
    mini.classList.remove('hidden');
  } else {
    mini.classList.add('hidden');
    full.classList.remove('hidden');

    const counter = document.getElementById('compare-counter');
    const translationTemplate = counter?.getAttribute('data-text') || '';
    if (counter && translationTemplate) {
      const text = translationTemplate.replace('{{ count }}', String(products.length));
      counter.textContent = text;
    }

    document.getElementById('compare-error-container')?.classList.toggle('hidden', !isLimitErrorVisible);

    // --- DOM reconciliation ---
    reconcileItems(itemsContainer, products, !!sharedProducts);

    // Render placeholders
    renderPlaceholders(itemsContainer, products.length);
  }
}

function reconcileItems(container: HTMLElement, products: CompareProduct[], isSharedMode: boolean) {
  const currentItems = Array.from(container.querySelectorAll('[data-product-id]'));
  const newIds = products.map((p) => p.id);

  // Remove old
  currentItems.forEach((el) => {
    const id = el.getAttribute('data-product-id');
    if (id && !newIds.includes(id)) el.remove();
  });

  // Add new
  products.forEach((p, index) => {
    let existingEl = container.querySelector(`[data-product-id="${p.id}"]`);
    if (!existingEl) {
      const newEl = createPanelItemElement(p);

      // Maintain order logic
      if (index === 0) {
        container.prepend(newEl);
      } else {
        const prevProduct = container.querySelector(`[data-product-id="${products[index - 1]?.id}"]`);
        if (prevProduct) {
          prevProduct.after(newEl);
        } else {
          container.prepend(newEl);
        }
      }

      // Listener for remove button
      newEl.querySelector('.remove-compare-btn')?.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        const updated = getCompareProducts().filter((prod) => prod.id !== id);

        if (isSharedMode) {
          sessionStorage.setItem('shared_compare_products', JSON.stringify(updated));
        } else {
          setCompareProducts(updated);
        }

        // Signal that something changed
        document.dispatchEvent(new CustomEvent('compare:updated'));
      });
    }
  });
}

function createPanelItemElement(p: CompareProduct): HTMLElement {
  const featuresHtml = p.features?.length
    ? `<p class="body-s hidden m:line-clamp-1">${p.features.join(' / ')}</p>`
    : '';

  const hasDiscount = p.comparePrice && p.comparePrice !== '' && p.comparePrice !== '0 €' && p.comparePrice !== p.price;
  const fromPriceText = window.compareTranslations?.from_price;
  const variants = p.variants || [];
  const getCleanPrice = (price: string | number): number => {
    return parseFloat(String(price).replace(/[^0-9.]/g, '')) || 0;
  };
  const availablePrices = variants.filter((v) => v.available).map((v) => getCleanPrice(v.price));
  const hasRange = availablePrices.length > 1 && Math.max(...availablePrices) > Math.min(...availablePrices);

  const fromLabel = hasRange ? `<span class="body-s">${fromPriceText} </span>` : '';

  const priceHtml = hasDiscount
    ? `<div class="flex flex-wrap items-center gap-2x pt-2x">
        <span>${fromLabel}<span class="badge-discount"><span class="heading-xs"> ${p.price}</span></span></span>
        <span class="body-s text-content-secondary line-through">${p.comparePrice}</span>
      </div>`
    : `<div class="pt-2x body-s"> ${fromLabel} <span class="heading-xs"> ${p.price}</span></div>`;

  return createElementFromHTML(`
    <div class="flex flex-1 basis-0 min-w-0 items-start gap-1x justify-between w-full compare-item-animate" data-product-id="${p.id}">
      <a href="${p.url}" class="flex flex-1 min-w-0 items-center gap-4x" data-product-card>
        <div class="w-[60px] h-[60px] m:w-[80px] m:h-[80px] shrink-0 bg-secondary rounded-s overflow-hidden flex items-center justify-center">
          <img src="${p.image}" class="max-w-full p-2x max-h-full object-contain mix-blend-multiply" loading="lazy" />
        </div>
        <div class="flex flex-col min-w-0">
          <div class="label-s text-content-primary line-clamp-1">${p.title}</div>
          ${featuresHtml}
          ${priceHtml}
        </div>
      </a>
      <button class="remove-compare-btn text-content-primary hover:bg-hover transition-colors p-1x rounded-pill" data-id="${p.id}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
  `);
}

function renderPlaceholders(container: HTMLElement, count: number) {
  container.querySelectorAll('.compare-placeholder').forEach((el) => el.remove());
  for (let i = count; i < MAX_COMPARE; i++) {
    const placeholder = createElementFromHTML(`
      <div class="compare-placeholder flex flex-1 basis-0 flex-col w-full py-5x m:py-0 items-center border-xs border-dashed border-tertiary rounded-m h-[62px] m:h-[80px] justify-center label-s text-content-tertiary bg-primary">
        ${i + 1}/${MAX_COMPARE}
      </div>
    `);
    container.appendChild(placeholder);
  }
}
